/**
 * Named native destinations. One helper per registered route, each returning
 * `linkOptions` checked against the registered route tree and, where the route
 * owns a search schema, against that schema's output type.
 *
 *   appLinks.salesOrder({ slug })   ──►  { to: '/sales-orders/$slug', params: { slug } }
 *          │                                        │
 *          └─ params are RAW DECODED strings        └─ the router does the encoding
 *
 * Search argument types are imported from `./routeSearch`; no field list is
 * restated here. A route with no `validateSearch` takes no search argument.
 */
import { linkOptions } from '@tanstack/react-router';
import { canonicalRouteSpecs } from './routeCatalog';
import type {
  AccountRegisterSearch,
  BIDashboardsSearch,
  DocumentEditorSearch,
  LoginSearch,
  PalletTagEditorSearch,
  SettingsAccountingSearch,
  SettingsEntitiesSearch,
  TabSearch,
  ViewLotTrackingSearch,
} from './routeSearch';

export type AppNavigationBehavior = Readonly<{ replace?: boolean; resetScroll?: boolean }>;

type SlugParams = Readonly<{ slug: string }>;

// The `{-$path}` segment and the legacy `path` query field share one name; the
// segment is the destination, so the search argument never carries `path`.
type AccountRegisterQuery = Readonly<Omit<AccountRegisterSearch, 'path'>>;

export const appLinks = {
  login: (search: LoginSearch = {}, behavior: AppNavigationBehavior = {}) =>
    linkOptions({ ...behavior, search, to: canonicalRouteSpecs.login.path }),
  landing: (behavior: AppNavigationBehavior = {}) => linkOptions({ ...behavior, to: canonicalRouteSpecs.landing.path }),
  notAMember: (behavior: AppNavigationBehavior = {}) =>
    linkOptions({ ...behavior, to: canonicalRouteSpecs.notAMember.path }),
  customerPortalProducts: (behavior: AppNavigationBehavior = {}) =>
    linkOptions({ ...behavior, to: canonicalRouteSpecs.customerPortalProducts.path }),
  purchaseOrders: (behavior: AppNavigationBehavior = {}) =>
    linkOptions({ ...behavior, to: canonicalRouteSpecs.purchaseOrders.path }),
  customerPortalPurchaseOrders: (behavior: AppNavigationBehavior = {}) => appLinks.purchaseOrders(behavior),
  purchaseOrder: ({ slug }: SlugParams, behavior: AppNavigationBehavior = {}) =>
    linkOptions({ ...behavior, params: { slug }, to: canonicalRouteSpecs.purchaseOrder.path }),
  editCustomerPortalPurchaseOrder: (params: SlugParams, behavior: AppNavigationBehavior = {}) =>
    appLinks.purchaseOrder(params, behavior),
  customerPortalInvoices: (behavior: AppNavigationBehavior = {}) =>
    linkOptions({ ...behavior, to: canonicalRouteSpecs.customerPortalInvoices.path }),
  viewCustomerPortalInvoice: ({ slug }: SlugParams, behavior: AppNavigationBehavior = {}) =>
    linkOptions({ ...behavior, params: { slug }, to: canonicalRouteSpecs.viewCustomerPortalInvoice.path }),
  settings: (behavior: AppNavigationBehavior = {}) =>
    linkOptions({ ...behavior, to: canonicalRouteSpecs.settings.path }),
  customerPortalSettings: (behavior: AppNavigationBehavior = {}) => appLinks.settings(behavior),
  searchResults: (behavior: AppNavigationBehavior = {}) =>
    linkOptions({ ...behavior, to: canonicalRouteSpecs.searchResults.path }),
  salesOrders: (behavior: AppNavigationBehavior = {}) =>
    linkOptions({ ...behavior, to: canonicalRouteSpecs.salesOrders.path }),
  salesOrder: ({ slug }: SlugParams, behavior: AppNavigationBehavior = {}) =>
    linkOptions({ ...behavior, params: { slug }, to: canonicalRouteSpecs.salesOrder.path }),
  // Index and detail names over ONE optional-segment route. `params` MERGES with the current
  // match, so the index name must name the parameter as undefined to drop the segment.
  aiInbox: (behavior: AppNavigationBehavior = {}) =>
    linkOptions({ ...behavior, params: { threadId: undefined }, to: canonicalRouteSpecs.aiInbox.path }),
  aiInboxThread: ({ threadId }: Readonly<{ threadId: string }>, behavior: AppNavigationBehavior = {}) =>
    linkOptions({ ...behavior, params: { threadId }, to: canonicalRouteSpecs.aiInbox.path }),
  buySellOrder: ({ slug }: SlugParams, behavior: AppNavigationBehavior = {}) =>
    linkOptions({ ...behavior, params: { slug }, to: canonicalRouteSpecs.buySellOrder.path }),
  growerProducts: (behavior: AppNavigationBehavior = {}) =>
    linkOptions({ ...behavior, to: canonicalRouteSpecs.growerProducts.path }),
  growerProduct: ({ slug }: SlugParams, behavior: AppNavigationBehavior = {}) =>
    linkOptions({ ...behavior, params: { slug }, to: canonicalRouteSpecs.growerProduct.path }),
  accountingReports: (search: TabSearch = {}, behavior: AppNavigationBehavior = {}) =>
    linkOptions({ ...behavior, search, to: canonicalRouteSpecs.accountingReports.path }),
  accounts: (behavior: AppNavigationBehavior = {}) =>
    linkOptions({ ...behavior, to: canonicalRouteSpecs.accounts.path }),
  accountRegisterNoPath: (search: AccountRegisterQuery = {}, behavior: AppNavigationBehavior = {}) =>
    linkOptions({ ...behavior, params: { path: undefined }, search, to: canonicalRouteSpecs.accountRegister.path }),
  accountRegister: (
    { path, ...search }: Readonly<{ path: string }> & AccountRegisterQuery,
    behavior: AppNavigationBehavior = {}
  ) => linkOptions({ ...behavior, params: { path }, search, to: canonicalRouteSpecs.accountRegister.path }),
  invoices: (behavior: AppNavigationBehavior = {}) =>
    linkOptions({ ...behavior, to: canonicalRouteSpecs.invoices.path }),
  payables: (behavior: AppNavigationBehavior = {}) =>
    linkOptions({ ...behavior, to: canonicalRouteSpecs.payables.path }),
  bill: ({ slug }: SlugParams, behavior: AppNavigationBehavior = {}) =>
    linkOptions({ ...behavior, params: { slug }, to: canonicalRouteSpecs.bill.path }),
  payments: (behavior: AppNavigationBehavior = {}) =>
    linkOptions({ ...behavior, to: canonicalRouteSpecs.payments.path }),
  printChecks: (behavior: AppNavigationBehavior = {}) =>
    linkOptions({ ...behavior, to: canonicalRouteSpecs.printChecks.path }),
  payment: ({ slug }: SlugParams, behavior: AppNavigationBehavior = {}) =>
    linkOptions({ ...behavior, params: { slug }, to: canonicalRouteSpecs.payment.path }),
  agingReports: (search: TabSearch = {}, behavior: AppNavigationBehavior = {}) =>
    linkOptions({ ...behavior, search, to: canonicalRouteSpecs.agingReports.path }),
  inventory: (behavior: AppNavigationBehavior = {}) =>
    linkOptions({ ...behavior, to: canonicalRouteSpecs.inventory.path }),
  productionRuns: (behavior: AppNavigationBehavior = {}) =>
    linkOptions({ ...behavior, to: canonicalRouteSpecs.productionRuns.path }),
  productionRun: ({ slug }: SlugParams, behavior: AppNavigationBehavior = {}) =>
    linkOptions({ ...behavior, params: { slug }, to: canonicalRouteSpecs.productionRun.path }),
  viewPickTickets: (behavior: AppNavigationBehavior = {}) =>
    linkOptions({ ...behavior, to: canonicalRouteSpecs.viewPickTickets.path }),
  editPickTicket: ({ slug }: SlugParams, behavior: AppNavigationBehavior = {}) =>
    linkOptions({ ...behavior, params: { slug }, to: canonicalRouteSpecs.editPickTicket.path }),
  viewReceivingTickets: (behavior: AppNavigationBehavior = {}) =>
    linkOptions({ ...behavior, to: canonicalRouteSpecs.viewReceivingTickets.path }),
  editReceivingTicket: ({ slug }: SlugParams, behavior: AppNavigationBehavior = {}) =>
    linkOptions({ ...behavior, params: { slug }, to: canonicalRouteSpecs.editReceivingTicket.path }),
  viewLotTracking: (
    { slug, warehouseId, ...search }: Readonly<{ slug: string; warehouseId: number }> & ViewLotTrackingSearch,
    behavior: AppNavigationBehavior = {}
  ) =>
    linkOptions({ ...behavior, params: { slug, warehouseId }, search, to: canonicalRouteSpecs.viewLotTracking.path }),
  inventoryTransfer: ({ slug }: SlugParams, behavior: AppNavigationBehavior = {}) =>
    linkOptions({ ...behavior, params: { slug }, to: canonicalRouteSpecs.inventoryTransfer.path }),
  inventoryTransfers: (behavior: AppNavigationBehavior = {}) =>
    linkOptions({ ...behavior, to: canonicalRouteSpecs.inventoryTransfers.path }),
  settingsAccounting: (search: SettingsAccountingSearch = {}, behavior: AppNavigationBehavior = {}) =>
    linkOptions({ ...behavior, search, to: canonicalRouteSpecs.settingsAccounting.path }),
  settingsBusiness: (search: TabSearch = {}, behavior: AppNavigationBehavior = {}) =>
    linkOptions({ ...behavior, search, to: canonicalRouteSpecs.settingsBusiness.path }),
  settingsEntities: (
    { id, ...search }: Readonly<{ id: number }> & SettingsEntitiesSearch,
    behavior: AppNavigationBehavior = {}
  ) => linkOptions({ ...behavior, params: { id }, search, to: canonicalRouteSpecs.settingsEntities.path }),
  pricingSheet: ({ id }: Readonly<{ id: number }>, behavior: AppNavigationBehavior = {}) =>
    linkOptions({ ...behavior, params: { id }, to: canonicalRouteSpecs.pricingSheet.path }),
  settingsAutomations: (behavior: AppNavigationBehavior = {}) =>
    linkOptions({ ...behavior, to: canonicalRouteSpecs.settingsAutomations.path }),
  settingsEmails: (behavior: AppNavigationBehavior = {}) =>
    linkOptions({ ...behavior, to: canonicalRouteSpecs.settingsEmails.path }),
  settingsOrders: (behavior: AppNavigationBehavior = {}) =>
    linkOptions({ ...behavior, to: canonicalRouteSpecs.settingsOrders.path }),
  settingsDocuments: (behavior: AppNavigationBehavior = {}) =>
    linkOptions({ ...behavior, to: canonicalRouteSpecs.settingsDocuments.path }),
  settingsPortal: (behavior: AppNavigationBehavior = {}) =>
    linkOptions({ ...behavior, to: canonicalRouteSpecs.settingsPortal.path }),
  settingsInventory: (search: TabSearch = {}, behavior: AppNavigationBehavior = {}) =>
    linkOptions({ ...behavior, search, to: canonicalRouteSpecs.settingsInventory.path }),
  warehouseDetails: ({ warehouseId }: Readonly<{ warehouseId: number }>, behavior: AppNavigationBehavior = {}) =>
    linkOptions({ ...behavior, params: { warehouseId }, to: canonicalRouteSpecs.warehouseDetails.path }),
  settingsTeam: (search: TabSearch = {}, behavior: AppNavigationBehavior = {}) =>
    linkOptions({ ...behavior, search, to: canonicalRouteSpecs.settingsTeam.path }),
  documentEditor: (search: DocumentEditorSearch = {}, behavior: AppNavigationBehavior = {}) =>
    linkOptions({ ...behavior, search, to: canonicalRouteSpecs.documentEditor.path }),
  palletTags: (behavior: AppNavigationBehavior = {}) =>
    linkOptions({ ...behavior, to: canonicalRouteSpecs.palletTags.path }),
  palletTagEditor: (search: PalletTagEditorSearch = {}, behavior: AppNavigationBehavior = {}) =>
    linkOptions({ ...behavior, search, to: canonicalRouteSpecs.palletTagEditor.path }),
  userProfile: (behavior: AppNavigationBehavior = {}) =>
    linkOptions({ ...behavior, to: canonicalRouteSpecs.userProfile.path }),
  businessIntelligence: (behavior: AppNavigationBehavior = {}) =>
    linkOptions({ ...behavior, to: canonicalRouteSpecs.businessIntelligence.path }),
  biDashboards: (
    { slug, ...search }: Readonly<{ slug: string }> & BIDashboardsSearch,
    behavior: AppNavigationBehavior = {}
  ) => linkOptions({ ...behavior, params: { slug }, search, to: canonicalRouteSpecs.biDashboards.path }),
  orderBoard: (behavior: AppNavigationBehavior = {}) =>
    linkOptions({ ...behavior, to: canonicalRouteSpecs.orderBoard.path }),
  scan: (behavior: AppNavigationBehavior = {}) => linkOptions({ ...behavior, to: canonicalRouteSpecs.scan.path }),
  scanLocation: (behavior: AppNavigationBehavior = {}) =>
    linkOptions({ ...behavior, to: canonicalRouteSpecs.scanLocation.path }),
  recipeBuilder: (behavior: AppNavigationBehavior = {}) =>
    linkOptions({ ...behavior, params: { slug: undefined }, to: canonicalRouteSpecs.recipeBuilder.path }),
  recipeBuilderEdit: ({ slug }: SlugParams, behavior: AppNavigationBehavior = {}) =>
    linkOptions({ ...behavior, params: { slug }, to: canonicalRouteSpecs.recipeBuilder.path }),
  scanDebug: (behavior: AppNavigationBehavior = {}) =>
    linkOptions({ ...behavior, to: canonicalRouteSpecs.scanDebug.path }),
};

export type AppLinkOptions = ReturnType<(typeof appLinks)[keyof typeof appLinks]>;
